import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  Box,
  Button,
  Chip,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DescriptionIcon from '@mui/icons-material/Description'
import ClearIcon from '@mui/icons-material/Clear'
import { PDFPreview } from '../PDFViewer'
import { Controller } from 'react-hook-form'

const baseStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  transition: 'border .3s ease-in-out, background-color .3s',
}

const activeStyle = {
  borderColor: '#2196f3',
}

const acceptStyle = {
  borderColor: '#00e676',
}

const rejectStyle = {
  borderColor: '#ff1744',
}

const hoverStyle = {
  backgroundColor: '#eeeeee',
}

const FileField = React.forwardRef(
  (
    { inputProps = {}, label, error, helperText, required, previewFullWidth = false, ...rest },
    ref
  ) => {
    const [files, setFiles] = useState([])
    const [previewFiles, setPreviewFiles] = useState([])

    const localRef = useRef(null)
    if (!ref) {
      ref = localRef
    }
    const thumbs = (
      <Grid
        container
        spacing={2}
        direction='row'
        justifyContent={inputProps.multiple ? undefined : 'center'}
      >
        {previewFiles.map((file, index) => (
          <Grid
            item
            xs={previewFullWidth ? 12 : 6}
            sm={previewFullWidth ? 12 : 4}
            md={previewFullWidth ? 12 : 3}
            key={index}
          >
            <Box
              display='flex'
              position='relative'
              flexDirection='column'
              alignItems='center'
              zIndex={100}
              width='100%'
            >
              <Paper square>
                {file.type === 'application/pdf' ? (
                  <PDFPreview file={file.preview} width={64} />
                ) : file.preview ? (
                  <img
                    style={{ minWidth: 64, maxWidth: '100%' }}
                    src={file.preview}
                    alt={file.name}
                  />
                ) : (
                  <DescriptionIcon fontSize='large' />
                )}
              </Paper>
              <Chip
                sx={{ mt: 1 }}
                label={file.name}
                variant='outlined'
                onDelete={() => removeFile(index)}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    )

    const handlePreviewFiles = (newFiles) => {
      for (const file of previewFiles) {
        URL.revokeObjectURL(file.preview)
      }
      const newPreviewFiles = []
      for (let i = 0; i < newFiles?.length; i++) {
        const file = newFiles[i]
        newPreviewFiles.push(
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      }
      setPreviewFiles(newPreviewFiles)
    }

    const onDrop = useCallback((acceptedFiles = [], force = false) => {
      if (force || (acceptedFiles && acceptedFiles.length > 0)) {
        const dt = new DataTransfer()

        if (!force && inputProps.multiple) {
          for (let i = 0; i < files.length; i++) {
            const file = files[i]
            if (!Object.keys(inputProps.accept || {})?.includes(file.type)) {
              continue
            }
            dt.items.add(file)
          }
        }

        for (let i = 0; i < acceptedFiles.length; i++) {
          const file = acceptedFiles[i]
          if (!Object.keys(inputProps.accept || {})?.includes(file.type)) {
            continue
          }
          dt.items.add(file)
          if (!inputProps.multiple) {
            break
          }
        }
        setFiles([...Array.from(dt.files)])
        handlePreviewFiles([...Array.from(dt.files)])
      }
    })

    useEffect(() => {
      onDrop(inputProps.value || inputProps.initialValue)
      return () => {
        for (const file of previewFiles) {
          URL.revokeObjectURL(file.preview)
        }
      }
    }, [])

    useEffect(() => {
      const dt = new DataTransfer()
      for (let i = 0; i < files.length; i++) {
        dt.items.add(files[i])
        if (!inputProps.multiple) {
          break
        }
      }
      inputProps.onChange({ target: { files } })
    }, [files])

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
      onDrop,
      accept: inputProps.accept,
    })

    const [hover, setHover] = useState(false)

    const style = useMemo(
      () => ({
        ...baseStyle,
        ...(isDragActive ? activeStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
        ...(hover ? hoverStyle : {}),
      }),
      [hover, isDragActive, isDragReject, isDragAccept]
    )

    const fileInputProps = getInputProps({
      ...inputProps,
      accept: Object.keys(inputProps.accept || {}),
    })

    function removeFile(index) {
      if (!ref?.current) {
        return
      }
      const newFiles = Array.from(files)
      newFiles.splice(index, 1)
      onDrop(newFiles, true)
    }

    function addFiles(e) {
      onDrop(e.target.files)
    }

    return (
      <Box display='block'>
        <FormLabel error={Boolean(error)}>
          {label} {Boolean(required) && '*'}
        </FormLabel>
        <Box
          position='relative'
          {...getRootProps({ style })}
          onMouseOver={() => {
            setHover(true)
          }}
          onMouseOut={() => {
            setHover(false)
          }}
        >
          <Typography variant='h3'>
            <CloudUploadIcon fontSize='inherit' />
          </Typography>
          <Typography variant='h6' align='center'>
            {inputProps.multiple ? 'Choose files...' : 'Choose a file...'}
          </Typography>

          {previewFiles?.length > 0 && (
            <Paper variant='outlined' sx={{ m: 1, p: 1, width: '100%' }}>
              {thumbs}
            </Paper>
          )}
          <input
            {...fileInputProps}
            ref={ref}
            type='file'
            style={{
              position: 'absolute',
              opacity: 0,
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
            onChange={addFiles}
            {...rest}
          />
        </Box>
        <FormHelperText error={Boolean(error)}>{helperText}</FormHelperText>
      </Box>
    )
  }
)
export default FileField
